import staticMedia from "../../lib/static";
import React from "react";
import { colors, media, spacing, gradientBackground } from "../theme";
import Paragraph from "./Paragraph";
import Headline from "./Headline";
import styled, { css } from "styled-components";
import { MediaRef, Media } from "../../lib/CmsApi";
import Content from "./Content";
import { responsiveBackgroundImage } from "../../lib/responsiveImage";
import getImageFocus from "../../lib/getImageFocus";
import { Image } from "../../lib/image";

const Wrapper = styled.header<{ withIcon?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${p =>
    p.withIcon
      ? css`
          @media screen and ${media.tablet} {
            ${spacing("margin-bottom", 160)}
          }
        `
      : spacing("margin-bottom", 160)}
`;

const FullImage = styled.div<{ image: Image }>`
  display: block;
  width: 100%;
  height: 640px;
  ${gradientBackground};
  background-size: cover;
  background-position: ${p => getImageFocus(p.image)};
  @media screen and ${media.tablet} {
    height: auto;
    min-height: 320px;
  }
  -webkit-print-color-adjust: exact !important;
  color-adjust: exact !important;
  ${p => responsiveBackgroundImage({ image: p.image })};
`;
const IconBox = styled.div`
  img {
    max-height: 100%;
    max-width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
const IntroBox = styled(Content)<{ withIcon?: boolean }>`
  width: 60%;
  max-width: 720px;
  position: absolute;
  bottom: ${p => (p.withIcon ? "calc(4px * 30)" : "-48px")};
  padding-top: calc(4px * 16) !important;
  padding-bottom: calc(4px * 16) !important;
  left: 10%;
  background-color: ${colors.anotherblue};
  color: ${colors.blue};
  @media screen and ${media.desktop} {
    padding: calc(4px * 16) calc(4px * 20) !important;
  }
  @media screen and ${media.tablet} {
    position: static;
    max-width: 100%;
    width: 100%;
    padding-top: calc(4px * 10) !important;
    padding-bottom: calc(4px * 10) !important;
  }
`;

const FlexRight = styled.div`
  position: relative;
  display: flex;
  align-self: flex-end;
  justify-content: flex-start;
  width: 25%;
  height: 192px;
  padding: calc(192px / 3.5);
  ${gradientBackground};
  @media screen and ${media.tablet} {
    position: absolute;
    right: 0;
    bottom: 0;
    max-height: 40%;
    padding: calc(4px * 8);
  }
  @media screen and ${media.mobile} {
    width: 30%;
    height: 130px;
  }
  @media screen and ${media.tiny} {
    padding: calc(4px * 4);
    width: 40%;
    height: 100px;
  }
`;
const StyledHeadline = styled(Headline)`
  line-height: 1.4;
  white-space: pre-wrap;
`;
const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

interface Props {
  image: Image;
  headline?: string;
  fallBackAlt: string;
  intro?: string;
  icon?: Media & MediaRef;
  className?: string;
}

export default function FullImageIntro(p: Props) {
  if (!p.image) {
    return null;
  }
  return (
    <Wrapper className={p.className} withIcon={!!p.icon}>
      <Wrap>
        <FullImage
          image={p.image}
          role="img"
          aria-label={p.image.alt || p.fallBackAlt}
        />
        {p.icon && (
          <FlexRight>
            <IconBox>
              <img src={staticMedia(p.icon)} alt={p.icon.alt} />
            </IconBox>
          </FlexRight>
        )}
      </Wrap>
      {p.headline && (
        <IntroBox padded withIcon={!!p.icon}>
          <StyledHeadline size={3} level={2}>
            {p.headline}
          </StyledHeadline>
          {p.intro && (
            <Paragraph small blue noMargin>
              {p.intro}
            </Paragraph>
          )}
        </IntroBox>
      )}
    </Wrapper>
  );
}

FullImageIntro.getImages = (p: Partial<Props>) => {
  const images = [];
  if (p.icon) images.push(p.icon);
  if (p.image) images.push(p.image);

  return images;
};
